<i18n lang="json">
{
  "zh-cn": {
    "title": "绑定企业微信",
    "intro": "绑定后可使用企业微信快速登录",
    "loading": "正在获取二维码...",
    "error": "获取绑定参数失败",
    "retry": "重试",
    "close": "关闭",
    "bindSuccess": "绑定成功",
    "bindFail": "绑定失败",
    "tips": "请使用企业微信扫描上方二维码完成绑定"
  },
  "zh-tw": {
    "title": "綁定企業微信",
    "intro": "綁定後可使用企業微信快速登入",
    "loading": "正在獲取二維碼...",
    "error": "獲取綁定參數失敗",
    "retry": "重試",
    "close": "關閉",
    "bindSuccess": "綁定成功",
    "bindFail": "綁定失敗",
    "tips": "請使用企業微信掃描上方二維碼完成綁定"
  },
  "en": {
    "title": "Bind Wecom",
    "intro": "After binding, you can use Wecom for quick login",
    "loading": "Loading QR Code...",
    "error": "Failed to get bind parameters",
    "retry": "Retry",
    "close": "Close",
    "bindSuccess": "Bind Successfully",
    "bindFail": "Bind Failed",
    "tips": "Please use Wecom to scan the QR code above to complete binding"
  }
}
</i18n>

<script setup lang="ts">
import * as ww from '@wecom/jssdk'
import { ElMessage } from 'element-plus'
import { nextTick, onUnmounted, ref } from 'vue'
import authApi from '@/api/modules/auth'
import * as wecomUtils from '@/utils/wecom'

defineOptions({
  name: 'WecomBindDialog',
})

const emits = defineEmits<{
  success: []
}>()

const { t } = useI18n()

const dialogVisible = ref(false)
const loading = ref(true)
const error = ref('')
const qrPanelEl = ref<HTMLElement | null>(null)
const qrPanelUnmount = ref<undefined | (() => void)>()
const state = ref(`bind_${Date.now()}`)

// 环境检测
const isWecomEnvironment = ref(false)
const loadingText = ref('正在获取二维码...')

// 使用 iframe 发起 OAuth2 授权（企业微信内部）
async function authorizeWithIframe() {
  try {
    loading.value = true
    loadingText.value = '正在发起企业微信授权...'
    dialogVisible.value = true

    // 使用 iframe 发起授权
    const { code } = await wecomUtils.authorizeWithIframe(authApi.getWecomLoginParams)

    loadingText.value = '正在绑定...'
    // 获取到 code 后，调用绑定接口
    await authApi.bindWecom({ code })

    ElMessage.success(t('bindSuccess'))
    dialogVisible.value = false
    emits('success')
  }
  catch (err: any) {
    console.error('企业微信绑定失败:', err)
    ElMessage.error(err.message || t('bindFail'))
    dialogVisible.value = false
  }
  finally {
    loading.value = false
    loadingText.value = '正在获取二维码...'
  }
}

// 获取企业微信二维码参数并展示
async function initWecomQRCode() {
  loading.value = true
  error.value = ''

  try {
    const response = await authApi.getWecomBindParams()

    // 清理旧面板
    if (qrPanelUnmount.value) {
      qrPanelUnmount.value()
      qrPanelUnmount.value = undefined
    }

    // 使用后端返回的参数创建登录面板
    const loginParams = response.data || {}
    loginParams.redirect_uri = window.location.href
    // 先结束 loading，确保模板渲染出容器
    loading.value = false
    await nextTick()

    if (qrPanelEl.value && Object.keys(loginParams).length > 0) {
      const panel = ww.createWWLoginPanel({
        el: qrPanelEl.value,
        params: loginParams,
        onLoginSuccess: async (res: any) => {
          if (res.code) {
            try {
              loading.value = true
              await authApi.bindWecom({ code: res.code })
              ElMessage.success(t('bindSuccess'))
              dialogVisible.value = false
              emits('success')
            }
            catch (err: any) {
              console.error('企业微信绑定失败:', err)
              ElMessage.error(err.message || t('bindFail'))
              // 绑定失败后重新获取二维码
              setTimeout(() => {
                initWecomQRCode()
              }, 1000)
            }
            finally {
              loading.value = false
            }
          }
        },
        onLoginFail: (err: any) => {
          console.error('企业微信绑定失败:', err)
          ElMessage.error(t('bindFail'))
        },
        onCheckWeComLogin: () => {
          // 检查企业微信登录环境
        },
      })
      qrPanelUnmount.value = () => panel.unmount()
    }
    else {
      throw new Error('缺少必要的绑定参数')
    }
  }
  catch (err: any) {
    console.error('初始化企业微信二维码失败:', err)
    error.value = err.message || t('error')
    loading.value = false
  }
}

// 重试
function handleRetry() {
  error.value = ''
  initWecomQRCode()
}

// 打开弹窗
async function open() {
  state.value = `bind_${Date.now()}`

  // 检查是否在企业微信环境中
  isWecomEnvironment.value = wecomUtils.isWecomEnvironment()

  if (isWecomEnvironment.value) {
    // 在企业微信环境中，使用 iframe 发起授权
    authorizeWithIframe()
  }
  else {
    // 不在企业微信环境中，显示二维码弹窗
    dialogVisible.value = true
    loading.value = true
    initWecomQRCode()
  }
}

// 关闭弹窗
function handleClose() {
  if (qrPanelUnmount.value) {
    qrPanelUnmount.value()
    qrPanelUnmount.value = undefined
  }
  dialogVisible.value = false
  loading.value = true
  error.value = ''
}

onUnmounted(() => {
  if (qrPanelUnmount.value) {
    qrPanelUnmount.value()
    qrPanelUnmount.value = undefined
  }
})

defineExpose({
  open,
})
</script>

<template>
  <ElDialog
    v-model="dialogVisible"
    :title="t('title')"
    width="500px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="min-h-400px w-full flex-col-stretch-center p-8">
      <div class="mb-4 text-center">
        <p class="text-sm text-muted-foreground">
          {{ t('intro') }}
        </p>
      </div>

      <!-- 二维码区域 -->
      <div class="relative flex-col-center">
        <div
          v-if="loading"
          class="size-200px flex-center border-2 border-gray-300 rounded-lg border-dashed"
        >
          <div class="text-center">
            <FaIcon name="i-mdi:loading" class="mb-2 animate-spin text-2xl" />
            <p class="text-sm text-gray-500">
              {{ loadingText }}
            </p>
          </div>
        </div>

        <div
          v-else-if="error"
          class="size-200px flex-col-center border-2 border-red-300 rounded-lg border-dashed"
        >
          <div class="text-center">
            <FaIcon name="i-mdi:alert-circle" class="mb-2 text-2xl text-red-500" />
            <p class="mb-3 text-sm text-red-500">
              {{ error }}
            </p>
            <FaButton size="sm" @click="handleRetry">
              {{ t('retry') }}
            </FaButton>
          </div>
        </div>

        <div v-else class="flex-col-center">
          <!-- 企业微信二维码容器 -->
          <div ref="qrPanelEl" class="border-2 border-gray-200 rounded-lg bg-white p-2" />
          <p class="mt-3 max-w-300px text-center text-xs text-gray-500">
            {{ t('tips') }}
          </p>
        </div>
      </div>
    </div>
  </ElDialog>
</template>

<style scoped>
.size-200px {
  width: 200px;
  height: 200px;
}

.max-w-200px {
  max-width: 200px;
}

.max-w-300px {
  max-width: 300px;
}

.min-h-400px {
  min-height: 400px;
}
</style>
